import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Eye, Edit2, Trash2, Search, X, Users, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

export function SkatersTab({ skaters, setSkaters, fetchSkaters, clubs, currentUser }) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [duplicateCount, setDuplicateCount] = useState(0);
  
  // Filters
  const [nameFilter, setNameFilter] = useState('');
  const [clubFilter, setClubFilter] = useState('all');
  
  // Form data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    gender: 'F',
    club_id: '',
    date_of_birth: '',
    age: '',
    over_18: null,
    nzfrs_number: '',
    artistic_number: '',
    top_medal_dance: '',
    top_medal_free: '',
    top_medal_figs: '',
    top_medal_dance_couples: '',
    top_medal_freeskating_pairs: ''
  });
  
  const isSuperUser = currentUser?.role === 'super';
  const currentUserId = currentUser?.user_id;

  // Watch for duplicate clusters in the user's accessible scope; refresh
  // whenever the skater list changes (new imports may surface new
  // candidates).
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/skaters/duplicates/count`, { withCredentials: true })
      .then(res => { if (!cancelled) setDuplicateCount(res.data?.count || 0); })
      .catch(() => { if (!cancelled) setDuplicateCount(0); });
    return () => { cancelled = true; };
  }, [skaters]);
  
  const canEditItem = (item) => {
    return isSuperUser || item.created_by === currentUserId || item.can_edit === true;
  };
  
  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      gender: 'F',
      club_id: '',
      date_of_birth: '',
      age: '',
      over_18: null,
      nzfrs_number: '',
      artistic_number: '',
      top_medal_dance: '',
      top_medal_free: '',
      top_medal_figs: '',
      top_medal_dance_couples: '',
      top_medal_freeskating_pairs: ''
    });
    setEditingItem(null);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare data - convert empty strings to null for optional numeric fields
    const submitData = {
      first_name: formData.first_name,
      last_name: formData.last_name,
      gender: formData.gender,
      club_id: formData.club_id || null,
      date_of_birth: formData.date_of_birth || null,
      age: formData.age ? parseInt(formData.age) : null,
      over_18: formData.over_18,
      nzfrs_number: formData.nzfrs_number || null,
      artistic_number: formData.artistic_number || null,
      top_medal_dance: formData.top_medal_dance ? parseInt(formData.top_medal_dance) : null,
      top_medal_free: formData.top_medal_free ? parseInt(formData.top_medal_free) : null,
      top_medal_figs: formData.top_medal_figs ? parseInt(formData.top_medal_figs) : null,
      top_medal_dance_couples: formData.top_medal_dance_couples ? parseInt(formData.top_medal_dance_couples) : null,
      top_medal_freeskating_pairs: formData.top_medal_freeskating_pairs ? parseInt(formData.top_medal_freeskating_pairs) : null
    };
    
    try {
      if (editingItem) {
        await axios.patch(`${API}/skaters/${editingItem.skater_id}`, submitData, { withCredentials: true });
        toast.success('Skater updated');
      } else {
        await axios.post(`${API}/skaters`, submitData, { withCredentials: true });
        toast.success('Skater created');
      }
      fetchSkaters();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(editingItem ? 'Failed to update skater' : 'Failed to create skater');
    }
  };
  
  const handleView = (skater) => {
    setViewingItem(skater);
    setViewDialogOpen(true);
  };
  
  const handleEdit = (skater) => {
    setEditingItem(skater);
    setFormData({
      first_name: skater.first_name || '',
      last_name: skater.last_name || '',
      gender: skater.gender || 'F',
      club_id: skater.club_id || '',
      date_of_birth: skater.date_of_birth || '',
      age: skater.age?.toString() || '',
      over_18: skater.over_18,
      nzfrs_number: skater.nzfrs_number || '',
      artistic_number: skater.artistic_number || '',
      top_medal_dance: skater.top_medal_dance?.toString() || '',
      top_medal_free: skater.top_medal_free?.toString() || '',
      top_medal_figs: skater.top_medal_figs?.toString() || '',
      top_medal_dance_couples: skater.top_medal_dance_couples?.toString() || '',
      top_medal_freeskating_pairs: skater.top_medal_freeskating_pairs?.toString() || ''
    });
    setDialogOpen(true);
  };
  
  const handleDeleteClick = (skaterId) => {
    setItemToDelete(skaterId);
    setDeleteDialogOpen(true);
  };
  
  const handleDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/skaters/${itemToDelete}`, { withCredentials: true });
      toast.success('Skater deleted');
      fetchSkaters();
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error) {
      toast.error('Failed to delete skater');
    } finally {
      setDeleting(false);
    }
  };
  
  // Filter skaters
  const filteredSkaters = skaters.filter(skater => {
    const matchesName = skater.first_name.toLowerCase().includes(nameFilter.toLowerCase());
    const matchesClub = clubFilter === 'all' || skater.club_id === clubFilter;
    return matchesName && matchesClub;
  });
  
  return (
    <div className="space-y-4">
      {duplicateCount > 0 && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3"
          data-testid="dedup-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <strong>{duplicateCount}</strong> possible duplicate skater
            {duplicateCount === 1 ? '' : 's'} detected across your competitions.
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/skaters-entries/duplicates')}
            className="border-amber-400 text-amber-900 hover:bg-amber-100"
            data-testid="dedup-banner-review-btn"
          >
            <Users className="mr-1.5 h-4 w-4" />
            Review &amp; Merge
          </Button>
        </div>
      )}
      {/* Filters and Add Button */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          {/* Name Filter */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input 
              placeholder="Filter by first name..."
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="pl-10"
              data-testid="skater-name-filter"
            />
            {nameFilter && (
              <button 
                onClick={() => setNameFilter('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-stone-600" />
              </button>
            )}
          </div>
          
          {/* Club Filter */}
          <Select value={clubFilter} onValueChange={setClubFilter}>
            <SelectTrigger className="w-[200px]" data-testid="skater-club-filter">
              <SelectValue placeholder="Filter by club" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clubs</SelectItem>
              {clubs.map(club => (
                <SelectItem key={club.club_id} value={club.club_id}>
                  {club.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-green-700 hover:bg-green-800"
          data-testid="add-skater-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Skater
        </Button>
      </div>
      
      {/* Skaters Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="skaters-table">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-stone-600">First Name</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">Last Name</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">Gender</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">Art No.</th>
                <th className="text-left py-3 px-4 font-medium text-stone-600">Club</th>
                <th className="text-center py-3 px-4 font-medium text-stone-600 w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSkaters.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-muted-foreground">
                    {skaters.length === 0 ? 'No skaters yet. Click "Add Skater" to create one.' : 'No skaters match your filters.'}
                  </td>
                </tr>
              ) : (
                filteredSkaters.map(skater => (
                  <tr key={skater.skater_id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="py-3 px-4 font-medium">{skater.first_name}</td>
                    <td className="py-3 px-4">{skater.last_name}</td>
                    <td className="py-3 px-4">{skater.gender === 'M' ? 'Male' : 'Female'}</td>
                    <td className="py-3 px-4 text-sm">{skater.artistic_number || '-'}</td>
                    <td className="py-3 px-4 text-sm">{skater.club_name || '-'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => handleView(skater)} 
                          className="action-icon"
                          title="View"
                          data-testid={`view-skater-${skater.skater_id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        {canEditItem(skater) && (
                          <>
                            <button 
                              onClick={() => handleEdit(skater)} 
                              className="action-icon"
                              title="Edit"
                              data-testid={`edit-skater-${skater.skater_id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button 
                              onClick={() => handleDeleteClick(skater.skater_id)} 
                              className="action-icon"
                              title="Delete"
                              data-testid={`delete-skater-${skater.skater_id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
      
      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Skater' : 'Add Skater'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6 pt-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-stone-800 border-b pb-2">Basic Information</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">First Name *</Label>
                  <Input 
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                    required
                    data-testid="skater-first-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Last Name *</Label>
                  <Input 
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                    required
                    data-testid="skater-last-name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gender *</Label>
                  <div className="flex gap-4 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio"
                        name="gender"
                        value="F"
                        checked={formData.gender === 'F'}
                        onChange={(e) => setFormData({...formData, gender: e.target.value})}
                        className="w-4 h-4 text-green-600"
                      />
                      <span>Female</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio"
                        name="gender"
                        value="M"
                        checked={formData.gender === 'M'}
                        onChange={(e) => setFormData({...formData, gender: e.target.value})}
                        className="w-4 h-4 text-green-600"
                      />
                      <span>Male</span>
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="club">Club</Label>
                  <Select 
                    value={formData.club_id || "none"} 
                    onValueChange={(value) => setFormData({...formData, club_id: value === "none" ? "" : value})}
                  >
                    <SelectTrigger data-testid="skater-club-select">
                      <SelectValue placeholder="Select a club" />
                    </SelectTrigger>
                    <SelectContent className="z-[200]">
                      <SelectItem value="none">No club</SelectItem>
                      {clubs.map(club => (
                        <SelectItem key={club.club_id} value={club.club_id}>
                          {club.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            
            {/* Age Information */}
            <div className="space-y-4">
              <h3 className="font-medium text-stone-800 border-b pb-2">Age Information</h3>
              <p className="text-sm text-muted-foreground">At least one of DOB, Age, or Over 18 may be required depending on competition type.</p>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <Input 
                    id="date_of_birth"
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    data-testid="skater-dob"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="age">Age</Label>
                  <Input 
                    id="age"
                    type="number"
                    min="0"
                    max="100"
                    value={formData.age}
                    onChange={(e) => setFormData({...formData, age: e.target.value})}
                    data-testid="skater-age"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Over 18?</Label>
                  <div className="flex gap-4 pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio"
                        name="over_18"
                        checked={formData.over_18 === true}
                        onChange={() => setFormData({...formData, over_18: true})}
                        className="w-4 h-4 text-green-600"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="radio"
                        name="over_18"
                        checked={formData.over_18 === false}
                        onChange={() => setFormData({...formData, over_18: false})}
                        className="w-4 h-4 text-green-600"
                      />
                      <span>No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Registration Numbers */}
            <div className="space-y-4">
              <h3 className="font-medium text-stone-800 border-b pb-2">Registration Numbers</h3>
              <p className="text-sm text-muted-foreground">Required for Area/State and National competitions.</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nzfrs_number">NZFRS Number</Label>
                  <Input 
                    id="nzfrs_number"
                    value={formData.nzfrs_number}
                    onChange={(e) => setFormData({...formData, nzfrs_number: e.target.value})}
                    data-testid="skater-nzfrs"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="artistic_number">Artistic Number</Label>
                  <Input 
                    id="artistic_number"
                    value={formData.artistic_number}
                    onChange={(e) => setFormData({...formData, artistic_number: e.target.value})}
                    data-testid="skater-artistic"
                  />
                </div>
              </div>
            </div>
            
            {/* Medal Tests */}
            <div className="space-y-4">
              <h3 className="font-medium text-stone-800 border-b pb-2">Medal Tests</h3>
              <p className="text-sm text-muted-foreground">Required for Area/State and National if skater enters the respective discipline.</p>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="top_medal_dance">Top Medal Dance</Label>
                  <Input 
                    id="top_medal_dance"
                    type="number"
                    min="0"
                    value={formData.top_medal_dance}
                    onChange={(e) => setFormData({...formData, top_medal_dance: e.target.value})}
                    data-testid="skater-medal-dance"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="top_medal_free">Top Medal Free</Label>
                  <Input 
                    id="top_medal_free"
                    type="number"
                    min="0"
                    value={formData.top_medal_free}
                    onChange={(e) => setFormData({...formData, top_medal_free: e.target.value})}
                    data-testid="skater-medal-free"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="top_medal_figs">Top Medal Figures</Label>
                  <Input 
                    id="top_medal_figs"
                    type="number"
                    min="0"
                    value={formData.top_medal_figs}
                    onChange={(e) => setFormData({...formData, top_medal_figs: e.target.value})}
                    data-testid="skater-medal-figs"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="top_medal_dance_couples">Top Medal Dance Couples</Label>
                  <Input 
                    id="top_medal_dance_couples"
                    type="number"
                    min="0"
                    value={formData.top_medal_dance_couples}
                    onChange={(e) => setFormData({...formData, top_medal_dance_couples: e.target.value})}
                    data-testid="skater-medal-dance-couples"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="top_medal_freeskating_pairs">Top Medal Freeskating Pairs</Label>
                  <Input 
                    id="top_medal_freeskating_pairs"
                    type="number"
                    min="0"
                    value={formData.top_medal_freeskating_pairs}
                    onChange={(e) => setFormData({...formData, top_medal_freeskating_pairs: e.target.value})}
                    data-testid="skater-medal-pairs"
                  />
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-green-700 hover:bg-green-800" data-testid="save-skater-btn">
                {editingItem ? 'Update Skater' : 'Create Skater'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Skater Details</DialogTitle>
          </DialogHeader>
          
          {viewingItem && (
            <div className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">First Name</p>
                  <p className="font-medium">{viewingItem.first_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Name</p>
                  <p className="font-medium">{viewingItem.last_name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gender</p>
                  <p className="font-medium">{viewingItem.gender === 'M' ? 'Male' : 'Female'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Club</p>
                  <p className="font-medium">{viewingItem.club_name || '-'}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-stone-800 mb-3">Age Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">DOB</p>
                    <p className="font-medium">{viewingItem.date_of_birth || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Age</p>
                    <p className="font-medium">{viewingItem.age ?? '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Over 18?</p>
                    <p className="font-medium">{viewingItem.over_18 === null ? '-' : viewingItem.over_18 ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-stone-800 mb-3">Registration Numbers</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">NZFRS Number</p>
                    <p className="font-medium">{viewingItem.nzfrs_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Artistic Number</p>
                    <p className="font-medium">{viewingItem.artistic_number || '-'}</p>
                  </div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-stone-800 mb-3">Medal Tests</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Dance:</span>
                    <span className="font-medium">{viewingItem.top_medal_dance ?? '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Free:</span>
                    <span className="font-medium">{viewingItem.top_medal_free ?? '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Figures:</span>
                    <span className="font-medium">{viewingItem.top_medal_figs ?? '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Dance Couples:</span>
                    <span className="font-medium">{viewingItem.top_medal_dance_couples ?? '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Pairs:</span>
                    <span className="font-medium">{viewingItem.top_medal_freeskating_pairs ?? '-'}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete Skater"
        description="Are you sure you want to delete this skater? This action cannot be undone."
        loading={deleting}
      />
    </div>
  );
}
